
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ReservationForm from '../../components/feature/ReservationForm';
import { updateSEO, addStructuredData } from '../../utils/seo';

export default function Hamacas() {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update SEO
    updateSEO({
      title: 'Zona de Hamacas - Castilletes Parque Tayrona | Experiencia Auténtica',
      description: 'Duerme en hamaca bajo las estrellas del Tayrona. Experiencia auténtica con mosquitero incluido, desayuno y acceso a todas las facilidades del hotel.',
      keywords: 'hamacas Tayrona, dormir hamaca, experiencia auténtica, mochileros Tayrona, alojamiento económico',
      canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/hamacas`
    });

    // Add structured data
    addStructuredData({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Zona de Hamacas Castilletes",
      "description": "Experiencia auténtica durmiendo en hamaca con mosquitero incluido en el Parque Nacional Tayrona",
      "category": "Alojamiento Económico",
      "brand": {
        "@type": "Brand",
        "name": "Castilletes Parque Tayrona"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "COP"
      },
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Mosquitero incluido",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Desayuno incluido",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Baños compartidos",
          "value": true
        }
      ]
    });
  }, []);

  const hammockImages = [
    'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/b5d174a064fe8165f9ca994ed1b51cb9.png',
    'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/5193af9f11a22151c54bafcd140a5d50.jpeg',
    'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/0f5e34887780e5fa423f39d3a02d10b9.jpeg'
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % hammockImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + hammockImages.length) % hammockImages.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  const includedServices = [
    { icon: 'ri-restaurant-line', name: 'Desayuno incluido' },
    { icon: 'ri-wifi-line', name: 'WiFi zona común' },
    { icon: 'ri-shower-line', name: 'Baños compartidos' },
    { icon: 'ri-swimming-pool-line', name: 'Acceso a piscina' },
    { icon: 'ri-parking-line', name: 'Parqueadero' }
  ];

  const campingRules = [
    'Check-in: 2:00 PM - Check-out: 11:00 AM',
    'Horario de silencio: 10:00 PM - 6:00 AM',
    'No se permiten mascotas (política del parque)',
    'Menores deben ir acompañados de adultos',
    'Prohibido fumar en áreas de descanso',
    'Mantener limpio el espacio común'
  ];

  const whatToBring = [
    'Ropa cómoda y ligera',
    'Protector solar biodegradable',
    'Repelente de insectos',
    'Linterna o lámpara frontal',
    'Botella de agua reutilizable',
    'Medicamentos personales'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-96 bg-cover bg-center" style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/1719a2b369f4f9422a491a0fda285a89.jpg')`
      }}>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Zona de Hamacas</h1>
            <p className="text-xl mb-6">Relájate al ritmo de la naturaleza</p>
          </div>
        </div>
      </section>

      {/* Hammock Option */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <div className="md:flex">
                <div className="md:w-1/2 relative">
                  {/* Carrusel de imágenes */}
                  <div className="relative h-64 md:h-full overflow-hidden">
                    <img 
                      src={hammockImages[currentImageIndex]}
                      alt={`Hamacas - Imagen ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover object-top"
                    />
                    
                    {/* Botones de navegación */}
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    
                    {/* Indicadores */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {hammockImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToImage(index)}
                          className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                            currentImageIndex === index 
                              ? 'bg-white' 
                              : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="md:w-1/2 p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Hamacas
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Vive la experiencia auténtica de dormir en hamaca en el corazón del Parque Nacional Tayrona. 
                    Perfecto para viajeros que buscan una conexión genuina con la naturaleza.
                  </p>

                  <div className="mb-8">
                    <h3 className="font-semibold text-gray-900 mb-4 text-lg">
                      Incluye:
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-3 text-xl"></i>
                        Mosquitero incluido
                      </li>
                      <li className="flex items-center text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-3 text-xl"></i>
                        Desayuno incluido
                      </li>
                      <li className="flex items-center text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-3 text-xl"></i>
                        Wi-Fi
                      </li>
                      <li className="flex items-center text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-3 text-xl"></i>
                        Baños compartidos
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Included Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Servicios Incluidos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {includedServices.map((service, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className={`${service.icon} text-2xl text-green-500`}></i>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">{service.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Camping Rules */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-information-line text-green-500 mr-2"></i>
                  Normas del Camping
                </h4>
                <ul className="space-y-3">
                  {campingRules.map((rule, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <i className="ri-arrow-right-s-line text-green-500 mr-2 mt-0.5"></i>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* What to Bring */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-suitcase-line text-green-500 mr-2"></i>
                  Qué Traer
                </h4>
                <ul className="space-y-3">
                  {whatToBring.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <i className="ri-arrow-right-s-line text-green-500 mr-2 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8">
              <div className="flex items-start">
                <i className="ri-alert-line text-yellow-600 text-xl mr-3 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">Información Importante</h4>
                  <p className="text-yellow-700 mb-2">
                    <strong>Entrada al Parque:</strong> Debes pagar la entrada al Parque Nacional Natural Tayrona por separado.
                  </p>
                  <p className="text-yellow-700 mb-2">
                    <strong>Parqueadero:</strong> El parque cobra adicional por vehículos (carros $19.000 COP, motos $13.500 COP).
                  </p>
                  <p className="text-yellow-700">
                    <strong>Reservas:</strong> Se requiere el 100% del pago para confirmar la reserva.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Reserva */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <ReservationForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
